import { Container, LoadingScreen } from '@/shared/components'
import useI18n from '@/shared/hooks/usei18n'
import { Route, Routes, useNavigate, useParams } from 'react-router-dom'
import EuaEmissionPriceOverviewWidget from './components/EuaEmissionPriceOverviewWidget'
import { useGetAllDeclarantCbamReports } from '@/features/cbam-reporting/hooks/cbamReports'
import { useSession } from '@/shared/hooks/useSession'
import CbamReportingPeriodCard from './components/CbamReportingPeriodCard'
import {
  Button,
  DataBlock,
  formatAddressLine,
  H2,
  PageSection
} from '@/common'
import { parsePeriodId } from '@artem-cbam-declarants/shared'
import { PencilIcon } from '@/common/components/icons'
import CbamReportDetailsPage from './pages/cbam-report-details'
import {
  cbamReportingPeriodConfig,
  getCbamReportStatusFromReport
} from '@/config/cbamReportingPeriods'
import { useAppState } from '@/shared/hooks/useAppState'
import { useEffect } from 'react'
import { useGetDeclarantById } from '@/features/cbam-reporting/hooks/declarants'
import DeclarantEditPage from './pages/declarants/pages/declarant-edit'

export default function CbamReporting() {
  const { declarantId } = useParams<{ declarantId: string }>()
  const { setSelectedDeclarantId } = useAppState()

  useEffect(() => {
    if (declarantId) {
      setSelectedDeclarantId(declarantId)
    }
  }, [declarantId, setSelectedDeclarantId])

  return (
    <Routes>
      <Route index element={<CbamReportingPage />} />
      <Route path="reports/:cbamReportingId/*" element={<CbamReportDetailsPage />} />
      <Route path="edit-declarant" element={<DeclarantEditPage />} />
    </Routes>
  )
}

function CbamReportingPage() {
  const { t } = useI18n()
  const navigate = useNavigate()
  const { setSelectedDeclarantId } = useAppState()

  const declarantId = useParams<{ declarantId: string }>().declarantId!

  const organizationId = useSession().session.organization.id
  const declarantQuery = useGetDeclarantById(organizationId, declarantId)
  const cbamReportQuery = useGetAllDeclarantCbamReports(organizationId, declarantId)

  const isLoadingDeclarant = declarantQuery.isLoading
  const declarant = declarantQuery.data

  const isLoadingCbamReports = cbamReportQuery.isLoading
  const cbamReports = cbamReportQuery.data

  useEffect(() => {
    setSelectedDeclarantId(declarantId)
  }, [declarantId, setSelectedDeclarantId])

  if (isLoadingDeclarant || isLoadingCbamReports) {
    return <LoadingScreen />
  }

  const countryName = t(`common:countriesByIsoAlpha2.${declarant?.country}`)

  const formattedAddress = formatAddressLine(
    declarant?.street,
    declarant?.streetNumber,
    declarant?.postCode,
    declarant?.city,
    countryName
  )

  const cbamReportsOptions = cbamReports
    ?.filter(cbamReport => cbamReportingPeriodConfig[cbamReport.periodId])
    .sort((prev, next) => {
      const prevPeriodId = prev.periodId
      const nextPeriodId = next.periodId
      const { startDate: prevStartDate } = parsePeriodId(prevPeriodId)
      const { startDate: nextStartDate } = parsePeriodId(nextPeriodId)
      return prevStartDate.getTime() - nextStartDate.getTime()
    })
    .map(cbamReport => ({
      id: cbamReport.id,
      label: cbamReportingPeriodConfig[cbamReport.periodId].label,
      value: cbamReport.id,
      status: getCbamReportStatusFromReport(cbamReport)
    }))

  return (
    <Container>
      <header>
        <H2>{declarant?.name}</H2>
      </header>
      <EuaEmissionPriceOverviewWidget />
      <PageSection
        title={t('cbamReporting:cbamReportingDashboard.declarantDetails.title')}
        actions={
          <Button
            variant="subtle"
            icon={<PencilIcon />}
            onClick={() => navigate(`/cbam-reporting/${declarantId}/edit-declarant`)}
          >
            {t('common:actions.edit')}
          </Button>
        }
      >
        <div className="grid grid-cols-3 gap-4">
          <DataBlock
            label={t('cbamReporting:cbamReportingDashboard.declarantDetails.dataSections.type')}
            data={t(`common:declarantType.${declarant?.type}.title`)}
          />
          <DataBlock
            label={t(
              'cbamReporting:cbamReportingDashboard.declarantDetails.dataSections.identificationNumber'
            )}
            data={declarant?.identificationNumber || '-'}
          />
          <DataBlock
            label={t('cbamReporting:cbamReportingDashboard.declarantDetails.dataSections.address')}
            data={formattedAddress}
          />
        </div>
      </PageSection>
      <PageSection title={t('cbamReporting:cbamReportingDashboard.reportingPeriods')}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {cbamReportsOptions?.map(cbamReport => (
            <div key={cbamReport.id}>
              <CbamReportingPeriodCard
                periodId={cbamReport.id}
                status={cbamReport.status}
                label={cbamReport.label}
              />
            </div>
          ))}
        </div>
      </PageSection>
    </Container>
  )
}
